import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AdminPartneredHotspotDto, AdminVenueEventDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'
import TextInput from '../ui/TextInput'
import AdminCentralDateTimeField from '../admin/AdminCentralDateTimeField'
import { formatAdminDateTimePreview } from '../../utils/datetimeInput'

interface PartneredHotspotModalProps extends BaseModalProps {
  hotspot: AdminPartneredHotspotDto | null
  onSaved: (hotspot: AdminPartneredHotspotDto) => void
  onDelete?: (hotspot: AdminPartneredHotspotDto) => void
}

interface SelectedEvent {
  publicId: string
  name: string
  venueName: string
}

const EVENT_SEARCH_PAGE_SIZE = 8

const PartneredHotspotModal = ({ isOpen, onClose, hotspot, onSaved, onDelete }: PartneredHotspotModalProps) => {
  const isEdit = hotspot !== null
  const [selectedEvent, setSelectedEvent] = useState<SelectedEvent | null>(null)
  const [startsAt, setStartsAt] = useState<string | null>(null)
  const [endsAt, setEndsAt] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<AdminVenueEventDto[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setSelectedEvent(hotspot ? { publicId: hotspot.event.publicId, name: hotspot.event.name, venueName: hotspot.venue.name } : null)
    setStartsAt(hotspot?.startsAt ?? null)
    setEndsAt(hotspot?.endsAt ?? null)
    setSearch('')
    setResults([])
    setError(null)
    setIsSaving(false)
  }, [isOpen, hotspot])

  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(async () => {
      try {
        const response = await adminService.getVenueEvents(1, EVENT_SEARCH_PAGE_SIZE, search.trim(), 'eventEndDate', 'desc')
        setResults(response.events)
      } catch {
        setResults([])
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [isOpen, search])

  const handleSelectEvent = (event: AdminVenueEventDto) => {
    setSelectedEvent({ publicId: event.publicId, name: event.name, venueName: event.venue.name })
    // Picking an event prefills the window with the event's own dates; admins can narrow it afterwards.
    setStartsAt(event.eventStartDate ?? event.startDate ?? null)
    setEndsAt(event.eventEndDate ?? event.endDate ?? null)
    setError(null)
  }

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const handleSubmit = async () => {
    if (!selectedEvent) {
      setError('Pick an event.')
      return
    }
    if (!startsAt || !endsAt) {
      setError('Start and end dates are required.')
      return
    }
    if (new Date(endsAt) <= new Date(startsAt)) {
      setError('The end date must be after the start date.')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const payload = { eventPublicId: selectedEvent.publicId, startsAt, endsAt }
      const saved = isEdit ? await adminService.updatePartneredHotspot(hotspot.publicId, payload) : await adminService.createPartneredHotspot(payload)
      onSaved(saved)
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.userMessage || e?.response?.data?.message || 'Failed to save partnered hotspot.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup role-modal partnered-hotspot-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>{isEdit ? 'Edit partnered hotspot' : 'Add partnered hotspot'}</h2>
        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <div className="partnered-hotspot-modal__selected">
            <span className="partnered-hotspot-modal__label">Event</span>
            {selectedEvent ? (
              <span>
                <strong>{selectedEvent.name}</strong> · {selectedEvent.venueName}
              </span>
            ) : (
              <span className="partnered-hotspot-modal__muted">No event selected</span>
            )}
          </div>

          <TextInput type="text" label="Search events" name="partneredHotspotEventSearch" value={search} onChange={setSearch} />

          <div className="partnered-hotspot-modal__results">
            {results.length === 0 ? (
              <div className="partnered-hotspot-modal__muted">No events found.</div>
            ) : (
              results.map(event => (
                <button
                  key={event.publicId}
                  type="button"
                  className={`partnered-hotspot-modal__result${selectedEvent?.publicId === event.publicId ? ' selected' : ''}`}
                  onClick={() => handleSelectEvent(event)}
                >
                  <span className="partnered-hotspot-modal__result-name">{event.name}</span>
                  <span className="partnered-hotspot-modal__muted">
                    {event.venue.name}
                    {' · '}
                    {formatAdminDateTimePreview(event.eventStartDate ?? event.startDate) ?? 'No start date'}
                    {' → '}
                    {formatAdminDateTimePreview(event.eventEndDate ?? event.endDate) ?? 'No end date'}
                  </span>
                </button>
              ))
            )}
          </div>

          <AdminCentralDateTimeField label="Partnered from" value={startsAt} onChange={setStartsAt} optional={false} allowClear={false} />
          <AdminCentralDateTimeField label="Partnered until" value={endsAt} onChange={setEndsAt} min={startsAt} optional={false} allowClear={false} />
        </form>

        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}

        <div className="modal-footer-content">
          {isEdit && onDelete && (
            <button type="button" className="styled-button danger" onClick={() => onDelete(hotspot)} disabled={isSaving}>
              Delete
            </button>
          )}
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="styled-button" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create hotspot'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PartneredHotspotModal
